<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import VIcon from "../Icon/VIcon.vue";

defineOptions({
  name: "VPaginator",
});

/**
 * Page buttons: previous, the page numbers (with "…" for long ranges) and next, as 28px pills. The current page is
 * light green and marked with aria-current. Previous and next are disabled on the first and last page.
 */
const {
  totalPages = 1,
  page = 1,
  previousText = "",
  nextText = "",
  loading = false,
  label = "",
} = defineProps<{
  totalPages?: number
  page?: number
  /** Accessible name of the previous button. */
  previousText?: string
  /** Accessible name of the next button. */
  nextText?: string
  loading?: boolean
  /** Name of the navigation landmark. Without it the buttons are a plain group, for a caller with its own nav. */
  label?: string
}>();

const emit = defineEmits<{
  (e: "update:page", value: number): void
}>();

const { t } = useI18n();

/** Up to this many pages are all shown; more collapse to the first, the last and the pages around the current one. */
const MAX_PLAIN_PAGES = 7;

type PageEntry = { kind: "page", number: number } | { kind: "gap", key: string };

const pages = computed<PageEntry[]>(() => {
  const last = Math.max(1, totalPages);
  if (last <= MAX_PLAIN_PAGES) {
    return Array.from({ length: last }, (_, index) => ({ kind: "page" as const, number: index + 1 }));
  }
  const current = Math.min(Math.max(1, page), last);
  let start = Math.max(2, Math.min(current - 1, last - 4));
  let end = Math.min(last - 1, Math.max(current + 1, 5));
  // A gap never hides a single page: "1 … 3" becomes "1 2 3".
  if (start === 3) {
    start = 2;
  }
  if (end === last - 2) {
    end = last - 1;
  }
  const entries: PageEntry[] = [{ kind: "page", number: 1 }];
  if (start > 2) {
    entries.push({ kind: "gap", key: "start" });
  }
  for (let number = start; number <= end; number++) {
    entries.push({ kind: "page", number });
  }
  if (end < last - 1) {
    entries.push({ kind: "gap", key: "end" });
  }
  entries.push({ kind: "page", number: last });
  return entries;
});

const isFirst = computed(() => page <= 1);
const isLast = computed(() => page >= totalPages);

function setPage(number: number) {
  if (loading || number === page || number < 1 || number > totalPages) {
    return;
  }
  emit("update:page", number);
}

const buttonClass = "inline-flex h-7 min-w-7 cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-white px-2 text-xs text-gray-800 transition-colors hover:bg-gray-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 disabled:pointer-events-none disabled:opacity-50 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-200 dark:hover:bg-dark-700";
const currentClass = "border-primary-100! bg-primary-50! font-semibold text-primary-700! dark:border-primary-800! dark:bg-primary-900/30! dark:text-primary-300!";
</script>

<template>
  <component
    :is="label ? 'nav' : 'div'"
    class="flex items-center gap-1"
    :aria-label="label || undefined"
    :aria-busy="loading || undefined"
  >
    <button
      type="button"
      :class="buttonClass"
      :disabled="isFirst || loading"
      :aria-label="previousText || t('base.pagination.previous')"
      data-testid="paginator-previous"
      @click="setPage(page - 1)"
    >
      <VIcon name="chevron-left" class="text-[11px]" aria-hidden="true" />
    </button>
    <template v-for="entry in pages" :key="entry.kind === 'page' ? entry.number : entry.key">
      <span v-if="entry.kind === 'gap'" class="inline-flex h-7 min-w-5 items-center justify-center text-xs text-gray-600 dark:text-gray-400" aria-hidden="true">…</span>
      <button
        v-else
        type="button"
        :class="[buttonClass, entry.number === page ? currentClass : undefined]"
        :aria-current="entry.number === page ? 'page' : undefined"
        :aria-label="t('base.pagination.page', { page: entry.number })"
        :disabled="loading && entry.number !== page"
        @click="setPage(entry.number)"
      >
        {{ entry.number }}
      </button>
    </template>
    <button
      type="button"
      :class="buttonClass"
      :disabled="isLast || loading"
      :aria-label="nextText || t('base.pagination.next')"
      data-testid="paginator-next"
      @click="setPage(page + 1)"
    >
      <VIcon name="chevron-right" class="text-[11px]" aria-hidden="true" />
    </button>
  </component>
</template>
